import { ChangeEvent, FC, Fragment } from 'react';
import { Blank, Button } from '@doda/io-react/common';
import {
  LocationHooksType,
  RouteStationHooksType,
} from 'components/SearchConditionSettingForm/types';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { SearchCheckboxItem } from 'components/SearchCheckboxList';
import styles from './index.module.scss';

/**
 * LocationContentsのプロパティを定義するインターフェース
 */
interface Props {
  /** ロケーションフック */
  location: LocationHooksType;
  /** 路線・駅フック */
  routeStation: RouteStationHooksType;
  /** 求人件数を更新する関数 */
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
  /** 勤務地モーダルを開くコールバック関数 */
  onClickOpenLocationModal: () => void;
  /** 路線・駅モーダルを開くコールバック関数 */
  onClickOpenStationModal: () => void;
}

/**
 * LocationContentsは勤務地および路線・駅の選択機能を提供するコンポーネント
 *
 * @param {Props} props - プロパティ
 * @returns {JSX.Element} - ロケーションコンテンツの要素
 */
export const LocationContents: FC<Props> = ({
  location,
  routeStation,
  updateJobCount,
  onClickOpenLocationModal,
  onClickOpenStationModal,
}) => {
  const {
    locationDisplayList,
    updateIsCheckedAreaList,
    updateIsCheckedPrefectureList,
    updateIsCheckedCityList,
    updateIsCheckedWardList,
  } = location;

  const {
    routeStationDisplayList,
    updateIsCheckedRouteList,
    updateIsCheckedStationList,
  } = routeStation;

  // 地域のチェックボックス押下処理
  const onChangeAreaListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedAreaList(event, selectedId);
    updateJobCount({ areaId: checkedList });
  };

  // 都道府県のチェックボックス押下処理
  const onChangePrefectureListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedPrefectureList(event, selectedId);
    updateJobCount({ prefectureId: checkedList });
  };

  // 市区町村チェックボックス押下処理
  const onChangeCityListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedCityList(event, selectedId);
    updateJobCount({ cityId: checkedList });
  };

  // 政令指定都市のチェックボックス押下処理
  const onChangeWardListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedWardList(event, selectedId);
    updateJobCount({ wardId: checkedList });
  };

  // 路線のチェックボックス押下時の処理
  const onChangeRouteListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedRouteList(event, selectedId);
    updateJobCount({ routeId: checkedList });
  };

  // 駅のチェックボックス押下時の処理
  const onChangeStationListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedStationList(event, selectedId);
    updateJobCount({ stationId: checkedList });
  };

  return (
    <div className={styles.searchConditionSettingForm__locationContents}>
      <Button
        as="button"
        onClick={onClickOpenLocationModal}
        size="S"
        variant="outline_gray"
      >
        勤務地を選択
        <Blank color="black" size="XS" />
      </Button>
      <Button
        as="button"
        onClick={onClickOpenStationModal}
        size="S"
        variant="outline_gray"
      >
        路線・駅を選択
        <Blank color="black" size="XS" />
      </Button>
      <div className={styles['location-check-list']}>
        {locationDisplayList.map((area) => (
          <Fragment key={area.id}>
            {area.isDisplay && (
              <SearchCheckboxItem
                title={area.name}
                isChecked={area.isChecked}
                value={area.id}
                onChange={(event) => onChangeAreaListCheckbox(event, area.id)}
                size="L"
              />
            )}
            {area.prefectureList.map((prefecture) => (
              <Fragment key={prefecture.id}>
                {prefecture.isDisplay && (
                  <SearchCheckboxItem
                    title={prefecture.name}
                    isChecked={prefecture.isChecked}
                    value={prefecture.id}
                    onChange={(event) =>
                      onChangePrefectureListCheckbox(event, prefecture.id)
                    }
                    size="L"
                  />
                )}
                {prefecture.cityList.map((city) => (
                  <Fragment key={city.id}>
                    {city.isDisplay && (
                      <SearchCheckboxItem
                        title={city.name}
                        isChecked={city.isChecked}
                        value={city.id}
                        onChange={(event) =>
                          onChangeCityListCheckbox(event, city.id)
                        }
                        size="L"
                      />
                    )}
                    {city.wardList.map((ward) => (
                      <Fragment key={ward.id}>
                        {ward.isDisplay && (
                          <SearchCheckboxItem
                            title={ward.name}
                            isChecked={ward.isChecked}
                            value={ward.id}
                            onChange={(event) =>
                              onChangeWardListCheckbox(event, ward.id)
                            }
                            size="L"
                          />
                        )}
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
        {routeStationDisplayList.map((route, index) => (
          <Fragment key={index}>
            {route.isDisplay && (
              <SearchCheckboxItem
                title={route.name}
                value={route.id}
                isChecked={route.isChecked}
                onChange={(event) => onChangeRouteListCheckbox(event, route.id)}
                size="L"
              />
            )}
            {route.stationList.map((station) => (
              <Fragment key={station.id}>
                {station.isDisplay && (
                  <SearchCheckboxItem
                    title={station.name}
                    value={station.id}
                    isChecked={station.isChecked}
                    onChange={(event) =>
                      onChangeStationListCheckbox(event, station.id)
                    }
                    size="L"
                  />
                )}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
